import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../../store/selectors/authSelectors";
import { patientsService } from "../../services/patientsService";
import { ROUTES } from "../../constants";
import {
  appendExternalIdQuery,
  getPatientProfilePath,
} from "../../utils/routeUtils";

const ExternalPatientPage: React.FC = () => {
  const { externalId } = useParams<{ externalId: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!externalId) {
      setError("Patient link is invalid.");
      return;
    }

    // Endpoint requires auth — send unauthenticated users to login first
    if (!isAuthenticated) {
      navigate(appendExternalIdQuery(ROUTES.LOGIN, externalId), {
        replace: true,
      });
      return;
    }

    let cancelled = false;

    const resolvePatient = async () => {
      try {
        const patient =
          await patientsService.getPatientByExternalId(externalId);

        if (cancelled) {
          return;
        }

        navigate(getPatientProfilePath(patient.id), { replace: true });
      } catch {
        if (!cancelled) {
          setError("Unable to find this patient. The link may be invalid.");
        }
      }
    };

    void resolvePatient();

    return () => {
      cancelled = true;
    };
  }, [externalId, isAuthenticated, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
        px: 2,
      }}
    >
      {error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
};

export default ExternalPatientPage;
